import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history, getLocale } from '@umijs/max';
import { message, notification } from './utils/message-manager';
import { encodeData, signMd5, generateRequestId } from './utils/utils';
import { isStandaloneApp, getTenantCode } from './utils/env';
import { reporter } from './utils/tracker';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number | string;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType, code, msg } =
        res as unknown as ResponseStructure & { code?: string; msg?: string };

      // Support new API format where code === '200' means success
      if (code === '200') {
        return;
      }

      if (!success) {
        const error: any = new Error(errorMessage || msg);
        error.name = 'BizError';
        error.info = { errorCode: errorCode || code, errorMessage: errorMessage || msg, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      const token = localStorage.getItem('token');
      const isV1 = config.url?.startsWith('/v1') || config.url?.startsWith('v1');

      const tenantCode = getTenantCode();

      // /v1 前缀的请求单独处理
      if (isV1) {
        config.headers = {
          ...config.headers,
          ...(token
            ? {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json;charset=UTF-8',
            }
            : {}),
          ...(tenantCode ? { tenantCode } : {}),
        };
        return config;
      }

      // 原有逻辑
      if (token) {
        config.headers = {
          ...config.headers,
          token: token,
        };
      }
      const terminalType = isStandaloneApp() ? 'standalone' : 'web';
      const requestId = generateRequestId();
      let isJsonPost =
        config.method?.toUpperCase() === 'POST' &&
        (config.headers?.['Content-Type'] as string | undefined)
          ?.includes('application/json') !== false &&
        config.data != null;
      if (config.data && config.data instanceof FormData) {
        isJsonPost = false;
      }
      // 签名规则:
      // POST + application/json: MD5( Base64(requestBody) + "_" + requestId + "_" + signKey )
      // 其他请求:               MD5( requestId + "_" + signKey )
      const sign = isJsonPost
        ? signMd5(requestId, encodeData(config.data))
        : signMd5(requestId);

      config.headers = {
        ...config.headers,
        language: getLocale(),
        terminalType,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        requestId,
        sign,
        ...(tenantCode ? { tenantCode } : {}),
      };

      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data, config } = response as unknown as ResponseStructure & { config: RequestOptions };
      const isV1 = config?.url?.startsWith('/v1') || config?.url?.startsWith('v1');

      if (isV1) {
        // RAGFlow API 响应处理 (假设 code: 0 为成功)
        if (data) {
          if (data.code === 0) {
            response.data = {
              ...data,
              success: true,
              status: 'ok',
            };
          } else {
            // 处理特定的错误码，例如 token 过期
            if (data.code === 10004 || data.code === 401) { // 假设
              message.error(data.msg || data.message || 'Unauthorized');
              reporter.stopHeartbeat();
              localStorage.removeItem('token');
              history.push('/user/login');
            }
            response.data = {
              ...data,
              success: false,
              errorMessage: data.msg || data.message,
            };
          }
        }
        return response;
      }

      // 统一处理 API 响应状态
      if (data) {
        if (data.code === '200') {
          response.data = {
            ...data,
            status: 'ok',
          };
        } else if (data.code === '10004' || data.code === '401') {
          reporter.stopHeartbeat();
          localStorage.removeItem('token');
          history.push('/user/login');
        } else if (data.code === '10005') {
          // 签名验证失败，提示后不做跳转
          message.error(data.msg || data.message || 'Invalid signature');
          response.data = {
            ...data,
            status: 'error',
          };
        } else if (data.code === '20501' || data.code === '20502') {
          // 租户相关异常：20501 租户不存在 / 20502 租户已被禁用
          message.error(data.msg || data.message || '租户异常，请重新登录');
          reporter.stopHeartbeat();
          localStorage.removeItem('token');
          history.push('/user/login');
        } else {
          response.data = {
            ...data,
            success: false,
            errorMessage: data.errorMessage || data.msg || data.message,
            status: 'error',
          };
        }
      }
      return response;
    },
  ],
};
